// src/pages/Technicians.js
import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { FiX, FiPlus, FiSettings, FiMaximize, FiMinimize, FiTrash2, FiEdit, FiSave, FiCheck, FiSearch, FiClock } from 'react-icons/fi';
import ListView from '../components/ListView';
import NewListModal from '../components/NewListModal';
import { AuthContext } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { useIsMobile } from '../hooks/useIsMobile';
import { useModal } from '../context/ModalContext';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

// --- HELPER: Format Time Since ---
const formatTimeSince = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return '< 1m';
    
    const minutes = Math.floor(diffInSeconds / 60);
    if (minutes < 60) return `${minutes}m`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    
    const days = Math.floor(hours / 24);
    return `${days}d`;
};

// --- MODAL COMPONENTS ---

const AddTechnicianModal = ({ isOpen, onClose, onAddTechnician, specializations, users, technicians }) => {
    const { user: currentUser } = useContext(AuthContext);
    const initialState = { phone: '', status: 'Active', skillLevel: 'Trainee', specializations: [] };
    const [technicianData, setTechnicianData] = useState(initialState);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [isAssignSpecModalOpen, setIsAssignSpecModalOpen] = useState(false);
    const overlayMousedownTarget = useRef(null);
    const { showAlert } = useModal();

    const linkableUsers = useMemo(() => {
        if (!users || !technicians || !currentUser) return [];
        const technicianUserIds = new Set(technicians.map(tech => tech.userId));
        const isGigaAdmin = currentUser.roles.includes('Giga Admin');

        const filtered = users.filter(user => {
            // If user object is populated (has _id), use it. Otherwise assume it is the ID string.
            const uid = user._id || user; 
            const isAlreadyTechnician = technicianUserIds.has(uid);
            if (isAlreadyTechnician) return false;

            if (isGigaAdmin) return true; 
            
            return user.departments.includes('Gate Services');
        });
    
        return filtered;
    }, [users, technicians, currentUser]);
  
    useEffect(() => {
        if (isOpen) {
            setTechnicianData(initialState);
            setSelectedUserId(linkableUsers.length > 0 ? linkableUsers[0]._id : '');
        }
    }, [isOpen, linkableUsers]);

    if (!isOpen) return null;

    const handleMouseDown = (e) => { overlayMousedownTarget.current = e.target; };
    const handleMouseUp = (e) => {
        if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) { onClose(); }
        overlayMousedownTarget.current = null;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setTechnicianData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedUserId) {
            showAlert('You must select a user account to link to this technician.', 'Validation Error');
            return;
        }
        if (technicianData.specializations.length === 0) {
            showAlert('Please select at least one specialization.', 'Validation Error');
            return;
        }
        const payload = { ...technicianData, userId: selectedUserId };
        onAddTechnician(payload);
        onClose();
    };

    const handleSaveSpecializations = (selectedNames) => {
        setTechnicianData(prev => ({...prev, specializations: selectedNames}));
    };
    const selectedSpecsText = technicianData.specializations.length > 0 ? `${technicianData.specializations.length} selected` : 'Select Specializations...';

    return (
        <>
            <AssignSpecializationsModal isOpen={isAssignSpecModalOpen} onClose={() => setIsAssignSpecModalOpen(false)} onSave={handleSaveSpecializations} allSpecializations={specializations} currentSpecializations={technicianData.specializations}/>
            {!isAssignSpecModalOpen && (
                <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
                    <div className="modal-content">
                        <div className="modal-header"><h3>Add New Technician</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
                        <form onSubmit={handleSubmit} className="modal-form">
                             <div className="form-group">
                                <label>User Account</label>
                                <select name="userId" value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} required className="form-select">
                                    <option value="" disabled>-- Select a User --</option>
                                    {linkableUsers.map(user => (
                                        <option key={user._id} value={user._id}>{user.name} ({user.email})</option>
                                    ))}
                                </select>
                                 {linkableUsers.length === 0 && <p className="form-group-description" style={{marginTop: '0.5rem'}}>No unlinked users found. Please add one in User Management first.</p>}
                            </div>
    
                            <div className="form-group"><label>Phone</label><input type="tel" name="phone" value={technicianData.phone} onChange={handleChange} placeholder="(XXX) XXX-XXXX" required /></div>
                            <div className="form-group"><label>Status</label><select name="status" value={technicianData.status} onChange={handleChange}><option>Active</option><option>On Leave</option><option>Inactive</option></select></div>
                            <div className="form-group"><label>Skill Level</label><select name="skillLevel" value={technicianData.skillLevel} onChange={handleChange}><option>Trainee</option><option>Junior</option><option>Senior</option><option>Master</option></select></div>
               
                            <div className="form-group"><label>Specializations</label><button type="button" className="btn btn-secondary btn-block" onClick={() => setIsAssignSpecModalOpen(true)} disabled={specializations.length === 0} title={specializations.length === 0 ? "Please add a specialization first" : "Select specializations"}>{specializations.length === 0 ? 'No specializations available' : selectedSpecsText}</button></div>
                            <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary">Save Technician</button></div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

const AssignSpecializationsModal = ({ isOpen, onClose, onSave, allSpecializations, currentSpecializations = [] }) => {
    const [selectedNames, setSelectedNames] = useState(currentSpecializations);
    const overlayMousedownTarget = useRef(null);
    useEffect(() => { if (isOpen) setSelectedNames(currentSpecializations); }, [isOpen, currentSpecializations]);
    if (!isOpen) return null;
    const handleToggleSelection = (specName) => { setSelectedNames(prev => prev.includes(specName) ? prev.filter(name => name !== specName) : [...prev, specName]); };
    const handleSave = () => { onSave(selectedNames); onClose(); };
    const handleMouseDown = (e) => { overlayMousedownTarget.current = e.target; };
    const handleMouseUp = (e) => { if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) { onClose(); } overlayMousedownTarget.current = null; };
    return (
        <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
            <div className="modal-content modal-content-slim">
                <div className="modal-header"><h3 className="modal-title">Select Specializations</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
                <div className="modal-body">
                    <div className="selection-list multi-select-list">
                        {allSpecializations.map(spec => { const isSelected = selectedNames.includes(spec.name); return (<div key={spec._id} className={`selection-list-item ${isSelected ? 'selected' : ''}`} onClick={() => handleToggleSelection(spec.name)}><div className="item-main-info"><p className="item-title">{spec.name}</p></div><div className="item-meta-info">{isSelected && <FiCheck className="selection-checkmark" />}</div></div>); })}
                    </div>
                </div>
                <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" onClick={handleSave}>Confirm ({selectedNames.length})</button></div>
            </div>
        </div>
    );
};

const ManageSpecializationsModal = ({ isOpen, onClose, specializations, onAdd, onUpdate, onDelete }) => {
    const [newSpecName, setNewSpecName] = useState('');
    const [editingSpec, setEditingSpec] = useState(null); const overlayMousedownTarget = useRef(null); const addInputRef = useRef(null);
    useEffect(() => { if (isOpen) { setNewSpecName(''); setEditingSpec(null); setTimeout(() => addInputRef.current?.focus(), 100); } }, [isOpen]);
    if (!isOpen) return null;
    const handleMouseDown = (e) => { overlayMousedownTarget.current = e.target; };
    const handleMouseUp = (e) => { if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) { onClose(); } overlayMousedownTarget.current = null; };
    const handleAddSubmit = (e) => { e.preventDefault(); if (!newSpecName.trim()) return; onAdd({ name: newSpecName }); setNewSpecName(''); };
    const handleUpdateSubmit = (e) => { e.preventDefault(); if (!editingSpec || !editingSpec.name.trim()) return; onUpdate(editingSpec._id, { name: editingSpec.name }); setEditingSpec(null); };
    const handleStartEdit = (spec) => { setEditingSpec({ ...spec }); };
    const handleCancelEdit = () => { setEditingSpec(null); };
    return (
        <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}><div className="modal-content modal-content-slim"><div className="modal-header"><h3 className="modal-title">Manage Specializations</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div><div className="modal-body"><div className="items-list" style={{ maxHeight: '250px', marginBottom: '1rem' }}>{specializations.length > 0 ? specializations.map(spec => (<div key={spec._id} className="list-item-row">{editingSpec && editingSpec._id === spec._id ? (<form onSubmit={handleUpdateSubmit} style={{ display: 'flex', width: '100%', gap: '0.5rem' }}><input type="text" value={editingSpec.name} onChange={(e) => setEditingSpec(s => ({...s, name: e.target.value}))} className="form-input" autoFocus/><button type="submit" className="btn btn-primary btn-sm"><FiSave/></button><button type="button" className="btn btn-secondary btn-sm" onClick={handleCancelEdit}><FiX/></button></form>) : (<><div className="item-info"><span className="item-title">{spec.name}</span></div><div className="item-actions"><button onClick={() => handleStartEdit(spec)} className="btn-icon-only"><FiEdit size={16} /></button><button onClick={() => onDelete(spec._id)} className="btn-icon-only btn-icon-danger"><FiTrash2 size={16} /></button></div></>)}</div>)) : (<p className="no-tasks-message">No specializations added yet.</p>)}</div><form onSubmit={handleAddSubmit}><div className="form-group"><label className="form-label">Add New Specialization</label><div style={{ display: 'flex', gap: '0.5rem' }}><input ref={addInputRef} type="text" name="newSpec" className="form-input" placeholder="e.g., Access Control" value={newSpecName} onChange={(e) => setNewSpecName(e.target.value)} /><button type="submit" className="btn btn-primary">Add</button></div></div></form></div><div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Done</button></div></div></div>
    );
};

// --- HELPER: Check Online Status ---
const isTechnicianOnline = (tech, onlineUserIds) => {
    if (!tech || !tech.userId) return false;
    // Check if tech.userId is an object (populated) or a string (ID)
    const uid = typeof tech.userId === 'object' ? tech.userId._id : tech.userId;
    return onlineUserIds.includes(uid);
};

// --- UPDATED: MOBILE CARD COMPONENT ---
const MobileTechnicianCard = ({ technician, onClick, canManage, isOnline, index = 0 }) => {
    // Calculate time since last update
    const timeSince = formatTimeSince(technician.lastStatusUpdate);

    // Use Regex to replace ALL spaces for class name generation
    const statusClass = technician.status.toLowerCase().replace(/\s+/g, '-');

    return (
        <div 
            className="mobile-technician-card" 
            onClick={onClick}
            style={{ 
                animation: 'slideFadeInWithGlow 0.4s ease-out backwards',
                animationDelay: `${index * 0.05}s`
            }}
        >
            <div className="mobile-technician-card-header">
                <div className="user-cell" style={{ gap: '0.75rem' }}>
                    <div className="list-view-avatar" style={{ position: 'relative' }}>
                        {technician.userId?.profilePictureUrl ? (
                            <img src={technician.userId.profilePictureUrl} alt={technician.name} className="list-view-avatar-img" />
                        ) : (
                            <span className="list-view-avatar-initials">{technician.name ? technician.name.charAt(0) : '?'}</span>
                        )}
                        {/* --- Online Indicator --- */}
                        {isOnline && <span className="online-indicator-dot" title="Online now"></span>}
                    </div>
                    <span className="mobile-technician-name">{technician.name}</span>
                </div>
                
                {/* Status + Time Badge Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className={`status-badge status-${statusClass}`}>{technician.status}</span>
                    
                    {/* SHOW BADGE ONLY IF INACTIVE */}
                    {timeSince && technician.status === 'Inactive' && (
                        <div className="time-tracking-badge" title={`Status changed ${timeSince} ago`}>
                            <FiClock size={10} />
                            <span>{timeSince}</span>
                        </div>
                    )}
                </div>
            </div>
            
            {canManage && (
                <div className="mobile-technician-card-body">
                    <p className="mobile-technician-specializations">
                        {technician.specializations.join(', ') || 'No specializations assigned'}
                    </p>
                </div>
            )}
        </div>
    );
};

// --- CONSTANTS & HELPERS ---
const ALL_TECHNICIAN_COLUMNS = [
    { header: 'Name', accessor: 'name' },
    { header: 'Email', accessor: 'email' },
    { header: 'Phone', accessor: 'phone' },
    { 
        header: 'Status', 
        accessor: 'status', 
        render: (tech) => {
            // Calculate time since last update using the helper function
            const timeSince = formatTimeSince(tech.lastStatusUpdate);
            // Use Regex to replace ALL spaces for class name generation
            const statusClass = tech.status.toLowerCase().replace(/\s+/g, '-');
            
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className={`status-badge status-${statusClass}`}>
                        {tech.status}
                    </span>
                    
                    {/* SHOW BADGE ONLY IF INACTIVE */}
                    {timeSince && tech.status === 'Inactive' && (
                        <div className="time-tracking-badge" title={`Status changed ${timeSince} ago`}>
                            <FiClock size={10} />
                            <span>{timeSince}</span>
                        </div>
                    )}
                </div>
            );
        }
    },
    { header: 'Skill Level', accessor: 'skillLevel' },
    { header: 'Specializations', accessor: 'specializations', render: (tech) => tech.specializations.join(', ') },
    { header: 'Joined', accessor: 'joinedDate', render: (tech) => new Date(tech.joinedDate).toLocaleDateString() },
];

const ALL_STATUSES = ['Active', 'On Leave', 'Inactive', 'Ticket In Progress']; // Added Ticket In Progress
const ALL_SKILL_LEVELS = ['Trainee', 'Junior', 'Senior', 'Master'];

// Default parameters to handle missing filters in user settings
const applyTechnicianFilters = (tech, filters) => {
    const { status = [], skillLevel = [], specializations = [] } = filters || {};
    
    if (status.length > 0 && !status.includes(tech.status)) return false;
    if (skillLevel.length > 0 && !skillLevel.includes(tech.skillLevel)) return false;
    if (specializations.length > 0 && !specializations.some(spec => tech.specializations.includes(spec))) return false;
    return true;
};

const Technicians = ({ isLoading, technicians, onAddTechnician, onUpdateTechnician, onDeleteMultipleTechnicians, specializations, onAddSpecialization, onUpdateSpecialization, onDeleteSpecialization, userDashboardSettings, onDashboardChange, users, onlineUserIds = [] }) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const { user } = useContext(AuthContext);
    
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [isManageSpecModalOpen, setManageSpecModalOpen] = useState(false);
    const [isConfigModalOpen, setConfigModalOpen] = useState(false);
    
    const [gridCols, setGridCols] = useState(12);
    const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
    const [fullscreenId, setFullscreenId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // --- Selection State ---
    const [selectedIds, setSelectedIds] = useState([]);

    const canManageTechnicians = useMemo(() => 
        user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), 
        [user]
    );

    const filteredMobileTechnicians = useMemo(() => {
        let sorted = [...technicians].sort((a, b) => a.name.localeCompare(b.name));
        if (!searchTerm) return sorted;
        const lowercasedTerm = searchTerm.toLowerCase();
        return sorted.filter(t => 
            t.name.toLowerCase().includes(lowercasedTerm) || 
            t.email.toLowerCase().includes(lowercasedTerm)
        );
    }, [technicians, searchTerm]);
    
    const lists = userDashboardSettings?.lists || [];
    const layouts = userDashboardSettings?.layouts || {};

    // ✅ NEW: Handler to save list state (columns, sort, widths)
    const handleListStateChange = useCallback((listId, newState) => {
        const newLists = lists.map(list => {
            if (list.id === listId) {
                return {
                    ...list,
                    ...newState
                };
            }
            return list;
        });
        onDashboardChange({ lists: newLists });
    }, [lists, onDashboardChange]);

    const handleLayoutChange = (layout, newLayouts) => { if (!fullscreenId) onDashboardChange({ layouts: newLayouts }); };
    const onBreakpointChange = (breakpoint, cols) => { setCurrentBreakpoint(breakpoint); setGridCols(cols); };

    // --- Selection Handlers ---
    const handleSelectOne = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (isChecked, idsOnPage) => {
        if (isChecked) {
            setSelectedIds(prev => {
                const uniqueIds = new Set([...prev, ...idsOnPage]);
                return Array.from(uniqueIds);
            });
        } else {
            setSelectedIds(prev => prev.filter(id => !idsOnPage.includes(id)));
        }
    };

    // Wrapper to clear selection after deleting
    const handleDeleteAndClearSelection = async (ids) => {
        await onDeleteMultipleTechnicians(ids);
        setSelectedIds([]);
    };

    const addList = (config) => {
        const newId = new Date().getTime().toString();
        const newList = { id: newId, ...config };
        const currentLayout = layouts[currentBreakpoint] || [];
        const newItemLayout = { i: newId, x: (currentLayout.length * 6) % gridCols, y: Infinity, w: 6, h: 10, minW: 4, minH: 6 };
        
        onDashboardChange({
            lists: [...lists, newList],
            layouts: { ...layouts, [currentBreakpoint]: [...currentLayout, newItemLayout] }
        });
    };

    const removeList = (idToRemove) => {
        if (fullscreenId === idToRemove) setFullscreenId(null);
        const newLists = lists.filter(list => list.id !== idToRemove);
        const newLayouts = { ...layouts };
        Object.keys(newLayouts).forEach(bp => { if (Array.isArray(newLayouts[bp])) newLayouts[bp] = newLayouts[bp].filter(item => item.i !== idToRemove); });
        onDashboardChange({ lists: newLists, layouts: newLayouts });
    };

    const toggleFullscreen = (listId) => setFullscreenId(prevId => (prevId === listId ? null : listId));

    if (isMobile) {
        return (
            <>
                <AddTechnicianModal isOpen={isAddModalOpen} onClose={() => setAddModalOpen(false)} onAddTechnician={onAddTechnician} specializations={specializations} users={users} technicians={technicians}/>

                <div className="page-container mobile-technicians-page">
                    <div className="page-header">
                        <h2 className="page-title">Technicians</h2>
                    </div>
                    <div className="mobile-filter-bar">
                        <div className="search-input-wrapper">
                            <FiSearch className="search-icon" />
                            <input 
                                type="search" 
                                className="search-input" 
                                placeholder="Search technicians..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="mobile-technician-list">
                        {filteredMobileTechnicians.length > 0 ? (
                            filteredMobileTechnicians.map((tech, index) => (
                                <MobileTechnicianCard 
                                    key={tech._id}
                                    technician={tech}
                                    onClick={() => navigate(`/technicians/${tech._id}`)}
                                    canManage={canManageTechnicians}
                                    // --- Pass online status ---
                                    isOnline={isTechnicianOnline(tech, onlineUserIds)}
                                    index={index}
                                />
                            ))
                        ) : (
                            <div className="no-tasks-message">
                                <p>No technicians found.</p>
                            </div>
                        )}
                    </div>
                    
                    {canManageTechnicians && (
                        <button className="fab-add-ticket" onClick={() => setAddModalOpen(true)} title="Add New Technician">
                            <FiPlus size={24} />
                        </button>
                    )}
                </div>
            </>
        );
    }

    return (
        <>
            <AddTechnicianModal isOpen={isAddModalOpen} onClose={() => setAddModalOpen(false)} onAddTechnician={onAddTechnician} specializations={specializations} users={users} technicians={technicians}/>
            <ManageSpecializationsModal isOpen={isManageSpecModalOpen} onClose={() => setManageSpecModalOpen(false)} specializations={specializations} onAdd={onAddSpecialization} onUpdate={onUpdateSpecialization} onDelete={onDeleteSpecialization}/>
            <NewListModal
                isOpen={isConfigModalOpen}
                onClose={() => setConfigModalOpen(false)}
                onAddList={addList}
                listType="Technician"
                columnOptions={ALL_TECHNICIAN_COLUMNS
                    .filter(c => canManageTechnicians || (c.accessor !== 'skillLevel' && c.accessor !== 'specializations'))
                    .map(c => ({ header: c.header, accessor: c.accessor }))
                }
                filterGroups={[
                    { title: 'Status', options: ALL_STATUSES, key: 'status' },
                    ...(canManageTechnicians ? [
                        { title: 'Skill Level', options: ALL_SKILL_LEVELS, key: 'skillLevel' },
                        { title: 'Specializations', options: specializations.map(s => s.name).sort(), key: 'specializations' },
                    ] : [])
                ]}
            />
 
            <div className={`page-container ${fullscreenId ? 'dashboard-fullscreen-mode' : ''}`}>
                <div className="page-header">
                    <div>
                        <h2 className="page-title">Technicians Dashboard</h2>
                        <p className="page-subtitle">Add, arrange, and customize your technician views.</p>
                    </div>
                    {canManageTechnicians && (
                        <div className="page-actions">
                            <button className="btn btn-secondary" onClick={() => setConfigModalOpen(true)}>Add New List</button>
                            <button className="btn btn-secondary" onClick={() => setManageSpecModalOpen(true)}><FiSettings size={16} className="btn-icon" /> Manage Specializations</button>
                            <button className="btn btn-primary" onClick={() => setAddModalOpen(true)}><FiPlus size={16} className="btn-icon" /> Add Technician</button>
                        </div>
                    )}
                </div>

                <div className="grid-container">
                    {isLoading ? null : (
                        <ResponsiveGridLayout
                            className="layout"
                            layouts={layouts}
                            onLayoutChange={handleLayoutChange}
                            onBreakpointChange={onBreakpointChange}
                            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                            rowHeight={30}
                            draggableHandle=".grid-card-header"
                            draggableCancel=".grid-card-remove-btn, .grid-card-expand-btn"
                            isDraggable={!fullscreenId}
                            isResizable={!fullscreenId}
                        >
                            {lists.map((list) => {
                                const isFullscreen = fullscreenId === list.id;
                                // Use safe filters object to prevent crash on undefined properties
                                const safeFilters = list.filters || {};
                                const filteredTechnicians = technicians.filter(tech => applyTechnicianFilters(tech, safeFilters));
                                
                                // --- UPDATED: Build columns for this list, including the online indicator ---
                                const columnsForThisList = ALL_TECHNICIAN_COLUMNS
                                    .filter(c => (list.columns || []).includes(c.accessor))
                                    .filter(c => canManageTechnicians || (c.accessor !== 'skillLevel' && c.accessor !== 'specializations'))
                                    .map(col => {
                                        if (col.accessor === 'name') {
                                            return {
                                                ...col,
                                                render: (tech) => {
                                                    const isOnline = isTechnicianOnline(tech, onlineUserIds);
                                                    return (
                                                        <div className="user-cell">
                                                            <div className="list-view-avatar" style={{ position: 'relative' }}>
                                                                {tech.userId?.profilePictureUrl ? (
                                                                    <img src={tech.userId.profilePictureUrl} alt={tech.name} className="list-view-avatar-img" />
                                                                ) : (
                                                                    <span className="list-view-avatar-initials">{tech.name ? tech.name.charAt(0) : '?'}</span>
                                                                )}
                                                                {/* --- Online Indicator --- */}
                                                                {isOnline && <span className="online-indicator-dot" title="Online now"></span>}
                                                            </div>
                                                            <span>{tech.name}</span>
                                                        </div>
                                                    );
                                                }
                                            };
                                        }
                                        return col;
                                    });

                                // Prepare saved state object
                                const savedState = {
                                    columnWidths: list.columnWidths || {},
                                    sortConfig: list.sortConfig || null,
                                    columnOrder: list.columnOrder || []
                                };

                                return (
                                    <div key={list.id} className={`grid-card ${isFullscreen ? 'is-fullscreen' : ''}`}>
                                        <div className="grid-card-header" onDoubleClick={() => toggleFullscreen(list.id)}>
                                            <h3 className="grid-card-title">{list.title}</h3>
                                            <div className="grid-card-actions">
                                                <button className="grid-card-expand-btn" onClick={() => toggleFullscreen(list.id)} title={isFullscreen ? 'Restore' : 'Expand'}>{isFullscreen ? <FiMinimize /> : <FiMaximize />}</button>
                                                <button className="grid-card-remove-btn" onClick={() => removeList(list.id)} title="Remove this list"><FiX /></button>
                                            </div>
                                        </div>
                                        <div className="grid-card-body">
                                            <ListView 
                                                items={filteredTechnicians} 
                                                columns={columnsForThisList} 
                                                onItemClick={(id) => navigate(`/technicians/${id}`)} 
                                                onDeleteItems={handleDeleteAndClearSelection} // --- Pass wrapper here
                                                searchKeys={['name', 'email']} 
                                                searchPlaceholder="Search this list by name or email..." 
                                                itemNoun="technician"
                                                enableSelection={canManageTechnicians}
                                                // --- Pass Selection Props Here ---
                                                selectedIds={selectedIds}
                                                onSelectOne={handleSelectOne}
                                                onSelectAll={handleSelectAll}
                                                // ✅ NEW: Saved State Props
                                                savedState={savedState}
                                                onStateChange={(updates) => handleListStateChange(list.id, updates)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </ResponsiveGridLayout>
                    )}
                    {lists.length === 0 && !isLoading && (
                        <div className="empty-state-container">
                            <div className="empty-state-content">
                                <h3>Your dashboard is empty.</h3>
                                <p>Get started by creating a custom view of your technicians.</p>
                                <button className="btn btn-primary" onClick={() => setConfigModalOpen(true)}>
                                    <FiPlus className="btn-icon" /> Add New List
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
export default Technicians;